let initialized = false;

const WAIT_MAX_RETRY = 20;
const WAIT_RETRY_DELAY = 500;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Returns true if Najva SDK is available.
 */
export const isNajvaReady = (): boolean => {
  return typeof window !== 'undefined' && typeof window.NAJVA?.getUserToken === 'function';
};

/**
 * Initializes Najva SDK.
 * Should be called once after the SDK has loaded.
 */
export const initializeNajva = (onUserSubscribed: (token: string) => void): boolean => {
  if (!isNajvaReady()) {
    return false;
  }

  const najva = window.NAJVA;

  if (!najva) {
    return false;
  }

  if (initialized) {
    return true;
  }

  najva.najvaUserSubscribed = onUserSubscribed;

  initialized = true;

  return true;
};

/**
 * Returns current Najva user token.
 */
export const getNajvaUserToken = async (): Promise<string | null> => {
  if (!isNajvaReady()) {
    return null;
  }

  const najva = window.NAJVA;

  if (!najva?.getUserToken) {
    return null;
  }

  try {
    return (await najva.getUserToken()) ?? null;
  } catch (error) {
    console.error('Failed to get Najva user token.', error);

    return null;
  }
};

/**
 * Wait until Najva SDK becomes ready and a token is available.
 */
export const waitForNajvaToken = async (): Promise<string | null> => {
  for (let retry = 0; retry < WAIT_MAX_RETRY; retry++) {
    const token = await getNajvaUserToken();

    if (token) {
      return token;
    }

    await sleep(WAIT_RETRY_DELAY);
  }

  return null;
};
